import { getLocalStorage, setLocalStorage } from './helper'
import { stripDeploymentBase, withDeploymentBase } from './deploymentPath'

export const getCurrentSubsidiaryId = () => getLocalStorage('smi')

/**
 * Persists a successful change-workspace response: the backend issues a fresh
 * access/refresh token pair scoped to the new subsidiary, plus its permissions/
 * theme/user info. Every caller of change-workspace must apply the full response
 * (not just `smi`) — an old subsidiary's access_token left in place gets rejected
 * as unauthorized on the very next request, which triggers the global axios
 * interceptor's unconditional refresh-or-logout and nukes the session. Shared so
 * WorkspaceSwitcherDrawer and SubsidiaryBoundary can't drift out of sync on this.
 */
export const applyWorkspaceSwitchResponse = (data: any, meta: any, workspaceName?: string, organizationName?: string) => {
  const { first_name, last_name, permission_details, profile_pic, theme_json, subsidiary_logo, subsidiary_master_id } = data || {}
  const { access_token, refresh_token } = meta || {}
  setLocalStorage('theme', typeof theme_json === 'string' ? theme_json : JSON.stringify(theme_json))
  setLocalStorage('logo', subsidiary_logo)
  setLocalStorage('permissions', JSON.stringify(permission_details))
  setLocalStorage('access_token', access_token)
  setLocalStorage('refresh_token', refresh_token)
  setLocalStorage('user_info', JSON.stringify({ firstName: first_name, lastName: last_name, profilePic: profile_pic }))
  setLocalStorage('smi', subsidiary_master_id)
  if (workspaceName) setLocalStorage('workspace_name', workspaceName)
  if (organizationName !== undefined) setLocalStorage('organization_name', organizationName || '')
}

export const parseSubsidiaryFromPath = (pathname: string) => stripDeploymentBase(pathname).split('/')[1] || ''

/**
 * An access_token can outlive `smi` (e.g. storage was partially cleared) —
 * building `/${currentSubId}${path}` in that state produces a bogus `/null/...`
 * URL. Fall back to /login so the app re-establishes a subsidiary instead.
 */
export const redirectWithSubsidiary = (currentSubId: string | null, path: string) => {
  window.location.replace(withDeploymentBase(currentSubId ? `/${currentSubId}${stripDeploymentBase(path)}` : '/login'))
}

export type UrlClassification = 'public' | 'missing' | 'match' | 'mismatch'

/**
 * Classifies a URL against the current subsidiary for the subsidiary-ID-in-URL
 * boundary (see SubsidiaryBoundary). Pure/synchronous, and deliberately doesn't
 * import the real route segment lists itself (that lives in routes.tsx, which
 * transitively pulls in the whole api layer) — callers pass them in, which keeps
 * this testable without mocking axios/routing.
 */
export const classifyUrl = (pathname: string, currentSubId: string | null, privateSegments: string[], publicSegments: string[]): UrlClassification => {
  const segment = parseSubsidiaryFromPath(pathname)
  if (publicSegments.includes(segment)) return 'public'
  if (!segment || privateSegments.includes(segment)) return 'missing'
  if (segment === currentSubId) return 'match'
  return 'mismatch'
}
